/**
 * OfferingDetail -- Loan Participations deal preview page.
 *
 * Sprint C-LSP-01 Wave 1B-3 (2026-05-14). The pre-commit diligence surface
 * for an FI participant evaluating a participation offering. Replaces the
 * Marketplace row -> immediate-Commit dialog flow with a real deal
 * preview where credit committee diligence happens. LSTA-aligned IOI
 * submission lives on this page (non-binding) -- the binding commitment
 * step is deferred to Wave 2 (lead-side allocation + participant
 * accept/decline).
 *
 * Tabs:
 *   - Overview: hero metrics, masked counterparty, commitment-deadline
 *     countdown, participation structure, distribution waterfall, full
 *     loan-detail surface (rate type, origination, maturity, payment
 *     frequency, servicing fee).
 *   - Collateral: consumes GET /offerings/:id/collateral. Per-asset table
 *     with originalValue / currentValue / valueChange / haircut /
 *     pledgeableValue / status. Aaim's CHA tier integration surfaces
 *     on this tab.
 *
 * IOI submission action lives in the page header CTA (renders as
 * "Indicate Interest" for non-LEAD callers, "Edit Offering" for the LEAD
 * arranger -- Wave 2 will light up the LEAD-side allocation review).
 */

import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { requestChangeHref } from '../../utils/request-change';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import {
  Heading,
  Card,
  Badge,
  Button,
  MetricCard,
  MetricCardGrid,
  Skeleton,
  Tabs,
  Dialog,
  Input,
  FormLabel,
  FormHelperText,
  DataGrid,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { components as LspComponents } from '@aaimplatform/institutional-types/loan-syndication-protocol';
import { loanSyndicationClient } from '../../api/client';
import { ELIGIBILITY_BADGE_VARIANT, ELIGIBILITY_LABEL } from './eligibility';
import { FeeScheduleDisclosure } from './FeeScheduleDisclosure';
import {
  formatCurrencyFull,
  formatPercent,
  formatRatioPercent,
  formatCurrencyCompact as formatCurrency,
  formatLeadDisplay,
  formatAssetType,
  OFFERING_STATUS_LABELS,
  labelFor,
} from './format';
import { resolveOwnParticipant, useOwnParticipants } from './own-participants';
import type { OwnParticipantState } from './own-participants';

type ParticipationOffering = LspComponents['schemas']['ParticipationOffering'];
type CollateralMonitoring = LspComponents['schemas']['CollateralMonitoring'];
type CollateralAssetStatus = LspComponents['schemas']['CollateralAssetStatus'];
type CovenantTerm = LspComponents['schemas']['CovenantTerm'];
type ComparableOffering = LspComponents['schemas']['ComparableOffering'];

// ---------------------------------------------------------------------------
// Loan-term labels. An absent term reads 'Not recorded'; the page never
// supplies a code the listing did not carry (it printed FIXED, MONTHLY and
// PRO_RATA for absent terms, and raw codes for present ones).
// ---------------------------------------------------------------------------

const TERM_NOT_RECORDED = 'Not recorded';

const RATE_TYPE_LABELS: Record<ParticipationOffering['loanDetails']['rateType'], string> = {
  FIXED: 'Fixed',
  VARIABLE: 'Variable',
};

const PAYMENT_FREQUENCY_LABELS: Record<ParticipationOffering['loanDetails']['paymentFrequency'], string> = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
};

const DISTRIBUTION_WATERFALL_LABELS: Record<
  NonNullable<ParticipationOffering['participationStructure']['distributionWaterfall']>,
  string
> = {
  PRO_RATA: 'Pro rata',
  SEQUENTIAL: 'Sequential',
};

/** A term code through its label map, or 'Not recorded' when the listing omits it. */
function termLabel(
  labels: Readonly<Record<string, string>>,
  value: string | null | undefined,
): string {
  if (!value) return TERM_NOT_RECORDED;
  return labelFor(labels, value);
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatDate(v: string | null | undefined): string {
  if (!v) return '--';
  return new Date(v).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
function formatDaysUntil(deadline: string | undefined): string {
  if (!deadline) return '--';
  const ms = new Date(deadline).getTime() - Date.now();
  if (ms < 0) return 'Closed';
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Closes today';
  return days === 1 ? '1 day' : `${days} days`;
}

function getAlertVariant(
  level: CollateralMonitoring['alertLevel'] | undefined,
): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (level) {
    case 'NORMAL':
      return 'success';
    case 'WATCH':
      return 'info';
    case 'WARNING':
      return 'warning';
    case 'CRITICAL':
      return 'error';
    default:
      return 'neutral';
  }
}

function getAssetStatusVariant(
  status: CollateralAssetStatus['status'] | undefined,
): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (status) {
    case 'ELIGIBLE':
      return 'success';
    case 'WATCH':
      return 'warning';
    case 'INELIGIBLE':
      return 'error';
    default:
      return 'neutral';
  }
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useOffering(offeringId: string | undefined) {
  return useQuery({
    queryKey: ['lsp-offering', offeringId],
    queryFn: async () => {
      if (!offeringId) throw new Error('offeringId is required');
      const { data, error } = await loanSyndicationClient.GET(
        '/api/v1/syndication/offerings/{offeringId}',
        { params: { path: { offeringId } } },
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ?? 'Failed to load offering',
        );
      }
      return data;
    },
    enabled: !!offeringId,
    staleTime: 30 * 1000,
    retry: false,
  });
}

function useCollateral(offeringId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['lsp-offering-collateral', offeringId],
    queryFn: async () => {
      if (!offeringId) throw new Error('offeringId is required');
      const { data, error } = await loanSyndicationClient.GET(
        '/api/v1/syndication/offerings/{offeringId}/collateral',
        { params: { path: { offeringId } } },
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ?? 'Failed to load collateral',
        );
      }
      return data;
    },
    enabled: !!offeringId && enabled,
    staleTime: 60 * 1000,
    retry: false,
  });
}

function useComparables(offeringId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['lsp-offering-comparables', offeringId],
    queryFn: async () => {
      if (!offeringId) throw new Error('offeringId is required');
      const { data, error } = await loanSyndicationClient.GET(
        '/api/v1/syndication/offerings/{offeringId}/comparables',
        { params: { path: { offeringId } } },
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ?? 'Failed to load comparables',
        );
      }
      return data;
    },
    enabled: !!offeringId && enabled,
    staleTime: 60 * 1000,
    retry: false,
  });
}

function useSubmitIoi() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      offeringId: string;
      participantId: string;
      indicatedAmount: number;
      notes?: string;
    }) => {
      const { data, error } = await loanSyndicationClient.POST(
        '/api/v1/syndication/offerings/{offeringId}/iois',
        {
          params: { path: { offeringId: vars.offeringId } },
          body: {
            participantId: vars.participantId,
            indicatedAmount: vars.indicatedAmount,
            notes: vars.notes,
          },
        },
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ?? 'IOI submission failed',
        );
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lsp-offering-iois'] });
      void queryClient.invalidateQueries({ queryKey: ['lsp-mine-iois'] });
    },
  });
}

// ---------------------------------------------------------------------------
// IOI submission dialog
// ---------------------------------------------------------------------------

interface IndicateInterestDialogProps {
  open: boolean;
  offering: ParticipationOffering | null;
  /** The caller's own registration, which the IOI body names as participantId. */
  participant: OwnParticipantState;
  onClose: () => void;
}

/** Why an IOI cannot be attributed to one registration, or null when it can. */
function participantBlocker(participant: OwnParticipantState): string | null {
  switch (participant.kind) {
    case 'ready':
      return null;
    case 'loading':
      return 'Reading your institution\'s network participant registration.';
    case 'failed':
      return 'Your institution\'s network participant registration could not be read, so an indication cannot be attributed to it.';
    case 'ambiguous':
      return `Your institution holds ${participant.count} active network participant registrations, so an indication cannot be attributed to one of them.`;
    case 'none':
      return 'IOI submission requires an active network participant registration for your institution.';
  }
}

function IndicateInterestDialog({
  open,
  offering,
  participant,
  onClose,
}: IndicateInterestDialogProps) {
  const participantId = participant.kind === 'ready' ? participant.participantId : undefined;
  const blocker = participantBlocker(participant);
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const mutation = useSubmitIoi();

  const minCommit = offering?.participationStructure.minimumCommitment ?? 0;
  const maxCommit =
    offering?.participationStructure.maximumCommitment ??
    offering?.remainingCapacity ??
    0;
  const numericAmount = Number(amount);
  const amountValid =
    Number.isFinite(numericAmount) &&
    numericAmount > 0 &&
    numericAmount >= minCommit &&
    (maxCommit === 0 || numericAmount <= maxCommit);
  const canSubmit =
    !!offering && !!participantId && amountValid && !mutation.isPending;

  function reset() {
    setAmount('');
    setNotes('');
    mutation.reset();
    onClose();
  }

  function handleSubmit() {
    if (!offering || !participantId) return;
    mutation.mutate(
      {
        offeringId: offering.offeringId,
        participantId,
        indicatedAmount: numericAmount,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          setTimeout(reset, 1500);
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
      }}
      title="Indicate Interest"
      size="lg"
    >
      {offering && (
        <div className="space-y-4">
          <div className="text-xs font-sans text-tertiary">
            An Indication of Interest (IOI) is non-binding. The lead arranger
            reviews the order book and issues an allocation; if you accept the
            allocation, it becomes a binding commitment at the offered size.
            CRT is not reserved at this step.
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-sans">
            <div>
              <div className="text-tertiary">Lead arranger</div>
              <div className="text-primary">{formatLeadDisplay(offering)}</div>
            </div>
            <div>
              <div className="text-tertiary">Loan amount</div>
              <div className="text-primary font-mono tabular-nums">
                {formatCurrencyFull(offering.loanDetails.loanAmount)}
              </div>
            </div>
            <div>
              <div className="text-tertiary">Rate</div>
              <div className="text-primary font-mono tabular-nums">
                {formatPercent(offering.loanDetails.interestRate)}
              </div>
            </div>
            <div>
              <div className="text-tertiary">Term</div>
              <div className="text-primary font-mono tabular-nums">
                {offering.loanDetails.termMonths} months
              </div>
            </div>
            <div>
              <div className="text-tertiary">Min indicated</div>
              <div className="text-primary font-mono tabular-nums">
                {formatCurrencyFull(minCommit)}
              </div>
            </div>
            <div>
              <div className="text-tertiary">Max indicated</div>
              <div className="text-primary font-mono tabular-nums">
                {maxCommit > 0 ? formatCurrencyFull(maxCommit) : '--'}
              </div>
            </div>
          </div>

          <FeeScheduleDisclosure
            arrangedAmount={offering.participationStructure.totalSyndicationAmount}
          />

          <div>
            <FormLabel htmlFor="ioi-amount">Indicated commitment (USD)</FormLabel>
            <Input
              id="ioi-amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min={minCommit}
              max={maxCommit > 0 ? maxCommit : undefined}
              step={1000}
              placeholder={String(minCommit)}
            />
            <FormHelperText>
              Non-binding. Min {formatCurrencyFull(minCommit)}.
              {maxCommit > 0 ? ` Max ${formatCurrencyFull(maxCommit)}.` : ''}
            </FormHelperText>
          </div>

          <div>
            <FormLabel htmlFor="ioi-notes">Rationale (optional)</FormLabel>
            <Input
              id="ioi-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
              placeholder="e.g. subject to credit committee 5/15"
            />
            <FormHelperText>
              Visible to the lead arranger only. Not visible to other
              participants.
            </FormHelperText>
          </div>

          {blocker && (
            <div className="bg-warning/10 border border-warning/30 rounded-sm p-3 text-xs font-sans text-secondary">
              {blocker}
              {participant.kind !== 'loading' && (
                <>
                  {' '}
                  {/* GH #1962: was "Contact Aaim Operations." -- a dead-end terminus.
                      It now files a tracked in-app request. */}
                  <Link
                    to={requestChangeHref({
                      surface: 'Syndication -- offering detail',
                      item:
                        participant.kind === 'none'
                          ? 'CrtParticipant activation for IOI submission'
                          : 'Loan Participations network membership record',
                      detail: `IOI submission is blocked: participant registration ${participant.kind}`,
                    })}
                    className="text-accent hover:underline"
                  >
                    {participant.kind === 'none'
                      ? 'Request participant activation'
                      : 'Request a review of the registration'}
                  </Link>
                </>
              )}
            </div>
          )}

          {mutation.isError && (
            <div className="bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
              <div className="text-xs font-sans text-error">
                {mutation.error instanceof Error
                  ? mutation.error.message
                  : 'IOI submission failed'}
              </div>
            </div>
          )}

          {mutation.isSuccess && (
            <div className="bg-growth/10 border border-growth/30 rounded-sm p-3 text-xs font-sans text-secondary">
              IOI submitted. The lead arranger will review and may issue an
              allocation.
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={reset}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {mutation.isPending ? 'Submitting...' : 'Submit IOI'}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

function OverviewTab({ offering }: { offering: ParticipationOffering }) {
  const remaining = offering.remainingCapacity ?? 0;
  const total = offering.participationStructure.totalSyndicationAmount ?? 0;
  const committed = total - remaining;
  const progress = total > 0 ? committed / total : 0;

  return (
    <div className="space-y-6 mt-6">
      <MetricCardGrid>
        <MetricCard
          label="Loan Amount"
          value={formatCurrency(offering.loanDetails.loanAmount)}
          change={`${offering.loanDetails.termMonths} mo at ${formatPercent(offering.loanDetails.interestRate)}`}
          changeType="neutral"
        />
        <MetricCard
          label="Remaining Capacity"
          value={formatCurrency(remaining)}
          change={`${formatRatioPercent(progress)} subscribed`}
          changeType="neutral"
        />
        <MetricCard
          label="LTV"
          value={formatRatioPercent(offering.collateralSummary?.ltvRatio)}
          change={formatAssetType(offering)}
          changeType="neutral"
        />
        <MetricCard
          label="Commit Deadline"
          value={formatDaysUntil(offering.commitmentDeadline)}
          change={formatDate(offering.commitmentDeadline)}
          changeType="neutral"
        />
      </MetricCardGrid>

      <Card className="p-6">
        <Heading level={2} size="card" className="mb-4">
          Loan terms
        </Heading>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm font-sans">
          <Field label="Lead arranger" value={formatLeadDisplay(offering)} />
          <Field
            label="Counterparty count"
            value={String(offering.counterpartyCount ?? offering.commitmentCount ?? 0)}
            mono
          />
          <Field
            label="Rate type"
            value={termLabel(RATE_TYPE_LABELS, offering.loanDetails.rateType)}
          />
          <Field
            label="Payment frequency"
            value={termLabel(PAYMENT_FREQUENCY_LABELS, offering.loanDetails.paymentFrequency)}
          />
          <Field
            label="Origination date"
            value={formatDate(offering.loanDetails.originationDate)}
          />
          <Field
            label="Maturity date"
            value={formatDate(offering.loanDetails.maturityDate)}
          />
          <Field
            label="Servicing fee"
            value={formatPercent(offering.loanDetails.servicingFeeRate)}
            mono
          />
          <Field
            label="Distribution waterfall"
            value={termLabel(
              DISTRIBUTION_WATERFALL_LABELS,
              offering.participationStructure.distributionWaterfall,
            )}
          />
        </div>
      </Card>

      <Card className="p-6">
        <Heading level={2} size="card" className="mb-4">
          Participation structure
        </Heading>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm font-sans">
          <Field
            label="Total syndicated"
            value={formatCurrencyFull(offering.participationStructure.totalSyndicationAmount)}
            mono
          />
          <Field
            label="Lead retained"
            value={formatCurrencyFull(offering.participationStructure.leadRetainedAmount)}
            mono
          />
          <Field
            label="Min commitment"
            value={formatCurrencyFull(offering.participationStructure.minimumCommitment)}
            mono
          />
          <Field
            label="Max commitment"
            value={
              offering.participationStructure.maximumCommitment !== undefined
                ? formatCurrencyFull(offering.participationStructure.maximumCommitment)
                : '--'
            }
            mono
          />
          <Field
            label="Max participants"
            value={String(offering.participationStructure.maximumParticipants ?? '--')}
            mono
          />
          <Field
            label="Total committed"
            value={formatCurrencyFull(committed)}
            mono
          />
          <Field
            label="Status"
            value={labelFor(OFFERING_STATUS_LABELS, offering.status)}
          />
          <Field
            label="Activated at"
            value={formatDate(offering.activatedAt)}
          />
        </div>
      </Card>

      <Card className="p-6">
        <Heading level={2} size="card" className="mb-4">
          Collateral summary
        </Heading>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm font-sans">
          <Field
            label="Primary asset class"
            value={termLabel({}, offering.collateralSummary?.primaryAssetClass)}
          />
          <Field
            label="Total value"
            value={formatCurrencyFull(offering.collateralSummary?.totalValue)}
            mono
          />
          <Field
            label="Asset count"
            value={String(offering.collateralSummary?.assetCount ?? '--')}
            mono
          />
          <Field
            label="LTV ratio"
            value={formatRatioPercent(offering.collateralSummary?.ltvRatio)}
            mono
          />
          <Field
            label="Last evaluated"
            value={formatDate(offering.collateralSummary?.lastEvaluatedAt)}
          />
        </div>
      </Card>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-xs text-tertiary mb-1">{label}</div>
      <div
        className={`text-primary ${mono ? 'font-mono tabular-nums' : 'font-sans'}`}
      >
        {value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Covenants tab
// ---------------------------------------------------------------------------

function getCovenantCategoryVariant(
  category: CovenantTerm['category'],
): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (category) {
    case 'FINANCIAL':
      return 'info';
    case 'OPERATIONAL':
      return 'success';
    case 'REPORTING':
      return 'warning';
    case 'OTHER':
    default:
      return 'neutral';
  }
}

function CovenantsTab({ offering }: { offering: ParticipationOffering }) {
  const covenants = offering.covenants ?? [];

  if (covenants.length === 0) {
    return (
      <div className="mt-6">
        <Card className="p-8 text-center">
          <div className="text-sm font-sans text-secondary">
            No covenants disclosed for this offering.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-4">
      {covenants.map((c) => (
        <Card key={c.id} className="p-6">
          <div className="flex items-start justify-between mb-4 gap-4">
            <Heading level={2} size="card">
              {c.label}
            </Heading>
            <Badge variant={getCovenantCategoryVariant(c.category)} size="sm">
              {c.category}
            </Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm font-sans">
            <Field label="Threshold" value={c.threshold ?? '--'} mono />
            <Field label="Frequency" value={c.frequency ?? '--'} />
            <Field label="Description" value={c.description ?? '--'} />
          </div>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comparables tab
// ---------------------------------------------------------------------------

function getComparableStatusVariant(
  status: ComparableOffering['status'],
): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (status) {
    case 'OPEN':
      return 'success';
    case 'FULLY_COMMITTED':
      return 'info';
    case 'ACTIVE':
      return 'success';
    case 'MATURED':
      return 'neutral';
    case 'DEFAULTED':
      return 'error';
    // term-register-skip: case label mirrors the OfferingStatus value CANCELLED in loan-syndication-protocol.openapi.yaml:2890; an identifier, not prose
    case 'CANCELLED':
      return 'neutral';
    default:
      return 'neutral';
  }
}

function formatStringDecimalCurrency(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '--';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  return formatCurrencyFull(numeric);
}

function formatSimilarity(score: number | null | undefined): string {
  return formatPercent(score);
}

function ComparablesTab({ offeringId }: { offeringId: string }) {
  const { data, isLoading, error } = useComparables(offeringId, true);

  const comparables: ComparableOffering[] = useMemo(
    () => data?.comparables ?? [],
    [data],
  );

  const columns = useMemo<ColumnDef<ComparableOffering, unknown>[]>(
    () => [
      {
        accessorKey: 'dealName',
        header: 'Deal',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans font-medium text-primary leading-normal">
            {(getValue() as string) || '--'}
          </span>
        ),
        size: 220,
      },
      {
        accessorKey: 'leadInstitutionName',
        header: 'Lead arranger',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-primary leading-normal">
            {(getValue() as string) || '--'}
          </span>
        ),
        size: 220,
      },
      {
        accessorKey: 'totalSyndicationAmount',
        header: 'Total syndication',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatStringDecimalCurrency(getValue() as string)}
          </span>
        ),
        size: 170,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={getComparableStatusVariant(row.original.status)} size="sm">
            {row.original.status}
          </Badge>
        ),
        size: 130,
      },
      {
        accessorKey: 'closedAt',
        header: 'Closed at',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {formatDate(getValue() as string | undefined)}
          </span>
        ),
        size: 140,
      },
      {
        accessorKey: 'similarityScore',
        header: 'Similarity',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatSimilarity(getValue() as number)}
          </span>
        ),
        size: 110,
      },
    ],
    [],
  );

  if (isLoading) {
    return (
      <div className="mt-6 space-y-4">
        <Card className="p-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full mb-2" />
          ))}
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error/5 border border-error/20 rounded-sm p-4 mt-6 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
        <div className="text-sm font-sans text-error">
          Failed to load comparables:{' '}
          {error instanceof Error ? error.message : 'unknown error'}
        </div>
      </div>
    );
  }

  if (comparables.length === 0) {
    return (
      <div className="mt-6">
        <Card className="p-8 text-center">
          <div className="text-sm font-sans text-secondary">
            No comparable offerings found for this deal.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <Card className="p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-recessed">
          <Heading level={2} size="card">
            Comparable offerings
          </Heading>
          <p className="text-xs font-sans text-tertiary mt-1">
            Ranked by similarity score across loan type, asset class, size band,
            and tenor.
          </p>
        </div>
        <DataGrid<ComparableOffering>
          data={comparables}
          columns={columns}
          enablePagination={false}
          hoverable
          getRowId={(row) => row.offeringId}
          className="w-full"
        />
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Collateral tab
// ---------------------------------------------------------------------------

function CollateralTab({ offeringId }: { offeringId: string }) {
  const { data, isLoading, error } = useCollateral(offeringId, true);

  const assets: CollateralAssetStatus[] = useMemo(() => {
    return data?.assets ?? [];
  }, [data]);

  const columns = useMemo<ColumnDef<CollateralAssetStatus, unknown>[]>(
    () => [
      {
        accessorKey: 'ticker',
        header: 'Ticker',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums">
            {(getValue() as string) || '--'}
          </span>
        ),
        size: 100,
      },
      {
        accessorKey: 'assetClass',
        header: 'Class',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-primary">
            {(getValue() as string) ?? '--'}
          </span>
        ),
        size: 140,
      },
      {
        accessorKey: 'originalValue',
        header: 'Original',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums">
            {formatCurrencyFull(getValue() as number)}
          </span>
        ),
        size: 140,
      },
      {
        accessorKey: 'currentValue',
        header: 'Current',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums">
            {formatCurrencyFull(getValue() as number)}
          </span>
        ),
        size: 140,
      },
      {
        accessorKey: 'haircut',
        header: 'Haircut',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums">
            {formatRatioPercent(getValue() as number)}
          </span>
        ),
        size: 100,
      },
      {
        accessorKey: 'pledgeableValue',
        header: 'Pledgeable',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums">
            {formatCurrencyFull(getValue() as number)}
          </span>
        ),
        size: 140,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={getAssetStatusVariant(row.original.status)} size="sm">
            {row.original.status}
          </Badge>
        ),
        size: 110,
      },
    ],
    [],
  );

  if (isLoading) {
    return (
      <div className="mt-6 space-y-4">
        <Skeleton className="h-24" />
        <Card className="p-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full mb-2" />
          ))}
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error/5 border border-error/20 rounded-sm p-4 mt-6 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
        <div className="text-sm font-sans text-error">
          Failed to load collateral:{' '}
          {error instanceof Error ? error.message : 'unknown error'}
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="mt-6 space-y-6">
      <MetricCardGrid>
        <MetricCard
          label="Current value"
          value={formatCurrency(data.currentValue)}
          change={`${formatPercent(data.valueChange)} vs original`}
          changeType={
            data.valueChange !== undefined && data.valueChange < 0
              ? 'negative'
              : 'positive'
          }
        />
        <MetricCard
          label="LTV"
          value={formatRatioPercent(data.ltvRatio)}
          change={
            data.ltvThreshold !== undefined
              ? `Threshold ${formatRatioPercent(data.ltvThreshold)}`
              : '--'
          }
          changeType="neutral"
        />
        <MetricCard
          label="Alert level"
          value={data.alertLevel ?? 'NORMAL'}
          change={
            data.alertLevel === 'CRITICAL' || data.alertLevel === 'WARNING'
              ? 'Action required'
              : 'No action'
          }
          changeType={
            data.alertLevel === 'CRITICAL'
              ? 'negative'
              : data.alertLevel === 'WARNING'
                ? 'negative'
                : 'positive'
          }
        />
        <MetricCard
          label="Next evaluation"
          value={formatDate(data.nextEvaluationAt)}
          change="Aaim CHA pipeline"
          changeType="neutral"
        />
      </MetricCardGrid>

      <Card className="p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-recessed flex items-center justify-between">
          <Heading level={2} size="card">
            Pledged assets
          </Heading>
          <Badge variant={getAlertVariant(data.alertLevel)} size="sm">
            {data.alertLevel ?? 'NORMAL'}
          </Badge>
        </div>
        {assets.length === 0 ? (
          <div className="p-8 text-sm font-sans text-tertiary text-center">
            No assets reported on this offering's collateral evaluation.
          </div>
        ) : (
          <DataGrid<CollateralAssetStatus>
            data={assets}
            columns={columns}
            enablePagination={false}
            hoverable
            getRowId={(row) => row.assetId ?? `${row.ticker}-${row.assetClass}`}
            className="w-full"
          />
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function OfferingDetail() {
  const { offeringId } = useParams<{ offeringId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<
    'overview' | 'collateral' | 'covenants' | 'comparables'
  >('overview');
  const [ioiOpen, setIoiOpen] = useState(false);

  const { data: offering, isLoading, error } = useOffering(offeringId);
  const ownParticipants = useOwnParticipants();
  const ownParticipantIds = ownParticipants.data?.participants.map((p) => p.participantId);
  const participant = resolveOwnParticipant({
    isLoading: ownParticipants.isLoading,
    isError: ownParticipants.isError,
    participantIds: ownParticipantIds,
  });

  // The viewer leads this offering when one of the institution's own
  // participant registrations is its leadParticipantId. A tenantId is not a
  // participant id and never matched, so a lead was offered "Indicate
  // Interest" on its own listing.
  const isLead = !!offering && !!ownParticipantIds?.includes(offering.leadParticipantId);
  // Until the registrations are read the page cannot tell a lead from a
  // participant, so the IOI action waits for the read.
  const canOfferIoi = !isLead && !ownParticipants.isLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-24 w-full" />
        <Card className="p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full mb-2" />
          ))}
        </Card>
      </div>
    );
  }

  if (error || !offering) {
    return (
      <div className="bg-error/5 border border-error/20 rounded-sm p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
        <div className="text-sm font-sans text-error">
          Failed to load offering:{' '}
          {error instanceof Error ? error.message : 'not found'}
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        className="inline-flex items-center gap-2 text-sm font-sans text-tertiary hover:text-primary transition-colors mb-3"
        onClick={() => navigate('/syndication/marketplace')}
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Marketplace
      </button>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the status badge
              and the identifier subtitle stay in the body, no redundant H1. */}
          <div className="flex items-center gap-3 mb-1">
            <Badge variant="info" size="sm">
              {labelFor(OFFERING_STATUS_LABELS, offering.status)}
            </Badge>
          </div>
          <p className="text-sm font-sans text-tertiary font-mono tabular-nums">
            {offering.offeringId}
          </p>
        </div>
        {canOfferIoi && offering.status === 'OPEN' && (
          <Button
            variant={offering.eligibility?.verdict === 'INELIGIBLE' ? 'secondary' : 'primary'}
            disabled={offering.eligibility?.verdict === 'INELIGIBLE'}
            onClick={() => setIoiOpen(true)}
          >
            {offering.eligibility?.verdict === 'INELIGIBLE' ? 'Ineligible' : 'Indicate Interest'}
          </Button>
        )}
      </div>

      {/* Per-viewer eligibility banner (LPM-2): present on cross-tenant
          marketplace listings; own-tenant and platform-admin views carry no
          assessment. INELIGIBLE here disables the IOI CTA above; the HARD
          block itself is enforced server-side at the binding step (commit /
          IOI accept), where an audited override attestation is available. */}
      {offering.eligibility && (
        <div className="mb-6 rounded-sm border border-recessed bg-elevated/30 p-4">
          <div className="flex items-center gap-3 mb-2">
            <span className="font-sans text-xs font-semibold uppercase tracking-wide text-tertiary">
              Eligibility for your institution
            </span>
            <Badge variant={ELIGIBILITY_BADGE_VARIANT[offering.eligibility.verdict]} size="sm">
              {ELIGIBILITY_LABEL[offering.eligibility.verdict]}
            </Badge>
          </div>
          <ul className="space-y-1">
            {offering.eligibility.reasons.map((r, i) => (
              <li key={i} className="font-sans text-xs text-secondary">
                <span className="font-semibold">{r.severity}</span> -- {r.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'collateral', label: 'Collateral' },
          { id: 'covenants', label: 'Covenants' },
          { id: 'comparables', label: 'Comparables' },
        ]}
        activeTab={activeTab}
        onTabChange={(id) =>
          setActiveTab(
            id as 'overview' | 'collateral' | 'covenants' | 'comparables',
          )
        }
        // The Collateral and Comparables panels fetch when they mount, so
        // arrow keys only move focus and Enter or Space selects.
        activation="manual"
      />

      {activeTab === 'overview' && <OverviewTab offering={offering} />}
      {activeTab === 'collateral' && (
        <CollateralTab offeringId={offering.offeringId} />
      )}
      {activeTab === 'covenants' && <CovenantsTab offering={offering} />}
      {activeTab === 'comparables' && (
        <ComparablesTab offeringId={offering.offeringId} />
      )}

      <IndicateInterestDialog
        open={ioiOpen}
        offering={offering}
        participant={participant}
        onClose={() => setIoiOpen(false)}
      />
    </div>
  );
}
